import * as React from 'react';
import type { BarrioStats as BarrioStatsType, BarrioInfo } from '../../types/barrios-landing';

interface BarrioStatsProps {
  barrio: BarrioInfo;
  stats: BarrioStatsType;
  dynamicPhrases: string[];
}

export function BarrioStats({ barrio, stats, dynamicPhrases }: BarrioStatsProps) {
  return (
    <section className="bs-stats">
      <style>{`
        .bs-stats {
          padding: 64px 24px;
          background: #fafaf7;
        }
        .bs-stats-inner {
          max-width: 960px;
          margin: 0 auto;
        }
        .bs-stats h2 {
          font-size: 26px;
          font-weight: 800;
          color: #1a1a1a;
          margin: 0 0 8px;
          text-align: center;
        }
        .bs-stats .section-sub {
          font-size: 16px;
          color: #666;
          text-align: center;
          margin: 0 0 32px;
        }
        .bs-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }
        .bs-stat-card {
          background: #fff;
          border-radius: 12px;
          border: 1px solid #eee;
          padding: 24px 20px;
          text-align: center;
        }
        .bs-stat-value {
          font-size: 32px;
          font-weight: 800;
          color: #1EAA25;
          margin: 0 0 4px;
        }
        .bs-stat-label {
          font-size: 14px;
          color: #666;
          margin: 0;
        }
        .bs-phrases {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 32px;
        }
        .bs-phrase {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 15px;
          color: #444;
          line-height: 1.5;
          padding: 12px 16px;
          background: #fff;
          border-radius: 10px;
          border: 1px solid #eee;
        }
        .bs-phrase i {
          color: #1EAA25;
          margin-top: 3px;
          flex-shrink: 0;
        }
        .bs-top-products {
          margin-top: 24px;
        }
        .bs-top-products h3 {
          font-size: 18px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 16px;
          text-align: center;
        }
        .bs-top-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        .bs-top-item {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #fff;
          border-radius: 10px;
          border: 1px solid #eee;
          padding: 12px 14px;
        }
        .bs-top-rank {
          font-size: 20px;
          font-weight: 800;
          color: #1EAA25;
          flex-shrink: 0;
          width: 28px;
          text-align: center;
        }
        .bs-top-img {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
          background: #f5f5f5;
        }
        .bs-top-info {
          min-width: 0;
        }
        .bs-top-name {
          font-size: 14px;
          font-weight: 600;
          color: #1a1a1a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .bs-top-qty {
          font-size: 12px;
          color: #888;
        }
        @media (max-width: 768px) {
          .bs-stats-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
          }
          .bs-stat-value {
            font-size: 24px;
          }
          .bs-top-grid {
            grid-template-columns: 1fr;
          }
          .bs-stats h2 {
            font-size: 22px;
          }
        }
      `}</style>
      <div className="bs-stats-inner">
        <h2>Datos de consumo en {barrio.name}</h2>
        <p className="section-sub">Lo que piden los vecinos de tu barrio</p>

        <div className="bs-stats-grid">
          <div className="bs-stat-card">
            <div className="bs-stat-value">{stats.customerCount}</div>
            <p className="bs-stat-label">Familias</p>
          </div>
          <div className="bs-stat-card">
            <div className="bs-stat-value">{stats.totalOrders.toLocaleString('es-AR')}</div>
            <p className="bs-stat-label">Pedidos entregados</p>
          </div>
          <div className="bs-stat-card">
            <div className="bs-stat-value">{stats.totalKilos.toLocaleString('es-AR')}</div>
            <p className="bs-stat-label">Kg entregados</p>
          </div>
        </div>

        {dynamicPhrases.length > 0 && (
          <div className="bs-phrases">
            {dynamicPhrases.map((phrase, i) => (
              <div key={i} className="bs-phrase">
                <i className="fa fa-chart-bar" />
                <span>{phrase}</span>
              </div>
            ))}
          </div>
        )}

        {stats.topProducts.length > 0 && (
          <div className="bs-top-products">
            <h3>Los más pedidos en {barrio.name}</h3>
            <div className="bs-top-grid">
              {stats.topProducts.map((product, i) => (
                <div key={product.product_id} className="bs-top-item">
                  <div className="bs-top-rank">{i + 1}</div>
                  {product.image_url && (
                    <img className="bs-top-img" src={product.image_url} alt={product.product_name} loading="lazy" />
                  )}
                  <div className="bs-top-info">
                    <div className="bs-top-name">{product.product_name}</div>
                    <div className="bs-top-qty">{product.total_units} {product.unit_short_name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
